import * as React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    // إرسال إلى لوحة /admin/site-health (lazy import — لا يكسر SSR ولا التحميل الأوّل).
    void import("@/lib/clientErrors")
      .then((m) => m.logBoundaryError(error, { componentStack: info.componentStack ?? undefined }))
      .catch(() => {});
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultErrorFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="t-display text-primary">!</h1>
        <h2 className="mt-4 t-h2 text-foreground">
          حدث خطأ غير متوقع
        </h2>
        <p className="mt-2 t-body-sm text-muted-foreground">
          {error.message || "نعتذر، حدث خلل أثناء عرض هذه الصفحة."}
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={() => {
              reset();
            }}
            className="inline-flex items-center justify-center border border-primary px-6 py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            إعادة المحاولة
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center border border-border px-6 py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition-colors hover:bg-secondary"
          >
            العودة للرئيسية
          </a>
        </div>
      </div>
    </div>
  );
}
